import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Shield, ShieldCheck, ShieldAlert, AlertTriangle, CheckCircle2, XCircle,
  Activity, RefreshCw, Trash2, Zap, GitBranch, Lock, Eye,
} from "lucide-react";
import {
  runArchitectureHealthCheck,
  getArchitectureViolations,
  clearArchitectureViolations,
  clearResponsibilityClaims,
  MODULE_REGISTRY,
  INTERNAL_MODULES,
  EXECUTION_ENGINE_OWNER,
  type ArchitectureHealthReport,
} from "@/data/moduleOwnership";
import { formatStandardTimestamp } from "@/lib/dateFormat";
import { ArchitectureHealthService } from "@/services/architecture-health.service";

export function ArchitectureHealthPanel() {
  const [report, setReport] = useState<ArchitectureHealthReport>(() => {
    const r = runArchitectureHealthCheck();
    // Persist initial run (fire-and-forget)
    ArchitectureHealthService.saveRun(r).catch(() => {});
    return r;
  });
  const [lastRun, setLastRun] = useState(new Date());

  const violations = useMemo(() => getArchitectureViolations(), [report]);

  // Restore any persisted violations on mount so history survives page refresh
  useEffect(() => {
    ArchitectureHealthService.getActiveViolations().then(persisted => {
      if (persisted.length > 0) {
        // Re-run to pick up any violations that were recorded before this session
        setReport(runArchitectureHealthCheck());
      }
    });
  }, []);

  const status = report.healthy && violations.length === 0
    ? "healthy"
    : violations.length > 3
      ? "critical"
      : "warning";

  const statusConfig = {
    healthy: { icon: ShieldCheck, label: "System Healthy", color: "text-success", bg: "bg-success/10 border-success/20" },
    warning: { icon: ShieldAlert, label: "Warnings Detected", color: "text-warning", bg: "bg-warning/10 border-warning/20" },
    critical: { icon: AlertTriangle, label: "Critical Issues", color: "text-destructive", bg: "bg-destructive/10 border-destructive/20" },
  }[status];

  function handleRerun() {
    const r = runArchitectureHealthCheck();
    // Persist run + any new violations (fire-and-forget)
    ArchitectureHealthService.saveRun(r).catch(() => {});
    ArchitectureHealthService.saveViolations(r.violations).catch(() => {});
    setReport(r);
    setLastRun(new Date());
  }

  function handleClear() {
    clearArchitectureViolations();
    // Bug 2 fix: also reset responsibility claims so duplicate-path checks re-trigger correctly
    clearResponsibilityClaims();
    // Clear persisted violations in DB (fire-and-forget)
    ArchitectureHealthService.clearViolations().catch(() => {});
    const r = runArchitectureHealthCheck();
    ArchitectureHealthService.saveRun(r).catch(() => {});
    setReport(r);
    setLastRun(new Date());
  }

  return (
    <div className="space-y-5">
      {/* Status Banner */}
      <div className={`flex items-center justify-between rounded-lg border p-4 ${statusConfig.bg}`}>
        <div className="flex items-center gap-3">
          <statusConfig.icon className={`h-6 w-6 ${statusConfig.color}`} />
          <div>
            <h2 className="text-lg font-semibold">{statusConfig.label}</h2>
            <p className="text-xs text-muted-foreground">
              Last checked {lastRun.toLocaleTimeString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleClear} className="gap-1.5">
            <Trash2 className="h-3.5 w-3.5" />
            Clear Violations
          </Button>
          <Button variant="outline" size="sm" onClick={handleRerun} className="gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" />
            Re-run Check
          </Button>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard icon={AlertTriangle} label="Violations" value={violations.length} accent={violations.length > 0 ? "destructive" : "default"} />
        <MetricCard icon={GitBranch} label="Modules Registered" value={MODULE_REGISTRY.length} />
        <MetricCard icon={Lock} label="Internal Guards" value={Object.keys(INTERNAL_MODULES).length} />
        <MetricCard icon={Zap} label="Execution Engine" value={EXECUTION_ENGINE_OWNER.replace(".ts", "")} />
      </div>

      {/* Health Checks */}
      <Card className="shadow-none">
        <CardHeader className="pb-2 px-4 pt-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
            Health Check Results
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="space-y-1">
            {report.checks.map((check) => (
              <div key={check.name} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                <div className="flex items-center gap-2.5">
                  {check.passed
                    ? <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
                    : <XCircle className="h-4 w-4 text-destructive shrink-0" />
                  }
                  <span className="text-sm font-medium">{formatCheckName(check.name)}</span>
                </div>
                <span className="text-xs text-muted-foreground">{check.detail}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Violation Monitor */}
      <Card className="shadow-none">
        <CardHeader className="pb-2 px-4 pt-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-muted-foreground" />
            Violation Monitor
            {violations.length > 0 && (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">{violations.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {violations.length === 0 ? (
            <div className="py-8 text-center">
              <ShieldCheck className="h-8 w-8 text-success/60 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No violations recorded</p>
              <p className="text-xs text-muted-foreground/70 mt-1">Architecture boundaries are intact</p>
            </div>
          ) : (
            <div className="space-y-1 max-h-[300px] overflow-y-auto">
              {violations.map((v, i) => (
                <div key={i} className="flex items-start gap-3 py-2 border-b border-border/50 last:border-0">
                  <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-semibold text-destructive">{v.domain}</span>
                      {/* Bug 1 fix: format raw ISO timestamp into readable form */}
                      <span className="text-[10px] text-muted-foreground">{formatStandardTimestamp(v.timestamp)}</span>
                    </div>
                    <p className="text-xs text-foreground">{v.caller} → {v.operation}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Module Registry */}
      <Card className="shadow-none">
        <CardHeader className="pb-2 px-4 pt-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Eye className="h-4 w-4 text-muted-foreground" />
            Module Registry
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="text-left py-2 px-3 font-medium text-muted-foreground">Domain</th>
                <th className="text-left py-2 px-3 font-medium text-muted-foreground">Owner</th>
                <th className="text-left py-2 px-3 font-medium text-muted-foreground hidden sm:table-cell">Writes To</th>
                <th className="text-left py-2 px-3 font-medium text-muted-foreground hidden md:table-cell">Reads From</th>
              </tr>
            </thead>
            <tbody>
              {MODULE_REGISTRY.map((m) => (
                <tr key={m.domain} className="border-b last:border-0">
                  <td className="py-1.5 px-3 font-medium">{m.domain.replace(/_/g, " ")}</td>
                  <td className="py-1.5 px-3 text-muted-foreground font-mono text-[11px]">{m.ownerFile}</td>
                  <td className="py-1.5 px-3 hidden sm:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {m.writesTo.map(w => (
                        <Badge key={w} variant="secondary" className="text-[9px] px-1 py-0">{w}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="py-1.5 px-3 hidden md:table-cell text-muted-foreground">
                    {m.readsFrom.length > 0 ? m.readsFrom.join(", ") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Internal Module Guards */}
      <Card className="shadow-none">
        <CardHeader className="pb-2 px-4 pt-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Lock className="h-4 w-4 text-muted-foreground" />
            Internal Module Guards
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="space-y-1">
            {Object.entries(INTERNAL_MODULES).map(([mod, config]) => (
              <div key={mod} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                <div>
                  <span className="text-sm font-mono font-medium">{mod}</span>
                  <p className="text-[11px] text-muted-foreground">Facade: {config.facade}</p>
                </div>
                <div className="flex flex-wrap gap-1 justify-end">
                  {config.allowedCallers.map(c => (
                    <Badge key={c} variant="outline" className="text-[9px] px-1.5 py-0">{c}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, accent = "default" }: {
  icon: React.ElementType; label: string; value: string | number; accent?: "default" | "destructive";
}) {
  return (
    <Card className="shadow-none">
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-1">
          <Icon className={`h-4 w-4 ${accent === "destructive" ? "text-destructive" : "text-muted-foreground"}`} />
          <span className="text-[11px] text-muted-foreground">{label}</span>
        </div>
        <p className={`text-lg font-bold ${accent === "destructive" ? "text-destructive" : ""}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function formatCheckName(name: string): string {
  return name.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}
